import React, { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectPath?: string;
}

// If MSAL is stuck in a non-resolved state for longer than this, force recovery
// by clearing state and redirecting to the login page. Must be > the token-queue
// timeout (25 s) + the init delay (100 ms) to give the normal flow a chance first.
const STUCK_TIMEOUT_MS = 30_000;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectPath = '/',
}) => {
  const { isAuthenticated, isInitialized, inProgress, logout } = useAuth();

  const isLoading = !isInitialized || inProgress !== 'none';

  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(async () => {
      console.warn('[ProtectedRoute] Auth stuck for 30 s — forcing session recovery');
      await logout();
    }, STUCK_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // logout is stable (useCallback), isLoading is a derived boolean — both fine as deps
  }, [isLoading, logout]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // If not authenticated, redirect to specified path
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};